import { fetchData } from "./util.js";

class InstructionsDatabase extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" });
    this.groupedData = null;
    this.featuredData = [];
    this.featuredTitle = null;
    this.featuredButtonTitle = null;
    this.background = null;
    this.cache = new Map();
    this.isRendered = false;
    this.showFeaturedBrands = false;
    this.selectedBrand = null;
  }

  async fetchShopifyTemplate(url) {
    if (this.cache.has(url)) {
      return this.cache.get(url).cloneNode(true);
    }

    const response = await fetch(url);
    const text = await response.text();
    const parser = new DOMParser();
    const doc = parser.parseFromString(text, "text/html");
    const result = doc.querySelector(".test");

    if (result) {
      this.cache.set(url, result.cloneNode(true));
    }

    return result;
  }

  static get observedAttributes() {
    return [
      "data",
      "featured",
      "featured-title",
      "featured-button-title",
      "background",
      "show-featured-brands",
    ];
  }

  loadStylesheets() {
    const cssUrls = [
      "/cdn/shop/t/26/assets/webcomponent.css",
      "/cdn/shop/t/26/assets/theme.css",
      "/cdn/shop/t/26/assets/tailwind.min.css",
    ];

    return Promise.all(
      cssUrls.map((href) => {
        return new Promise((resolve, reject) => {
          const link = document.createElement("link");
          link.rel = "stylesheet";
          link.href = `${href}?v=${Math.floor(Date.now() / 1000)}`;
          link.onload = resolve;
          link.onerror = reject;
          this.shadowRoot.appendChild(link);
        });
      })
    );
  }

  async attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    if (name === "data") {
      this.data = JSON.parse(newValue);
      this.groupedData = this.groupData(this.data);
    } else if (name === "featured") {
      this.featuredData = JSON.parse(newValue);
      this.groupedFeaturedData = this.groupFeatured(this.featuredData);
    } else if (name == "featured-title") {
      this.featuredTitle = newValue;
    } else if (name == "featured-button-title") {
      this.featuredButtonTitle = newValue;
    } else if (name == "background") {
      this.background = newValue;
    } else if (name == "show-featured-brands") {
      this.showFeaturedBrands = newValue;
    }
    if (this.groupedData && this.groupedFeaturedData && !this.isRendered) {
      await this.loadStylesheets();
      this.render();
    }
  }

  groupData(data) {
    return Object.values(
      data.reduce((acc, item) => {
        if (!item.hasInstructions) return acc;

        const group = item.name[0].toUpperCase();
        acc[group] ??= {
          group,
          children: [],
          first: item.first,
          image: item.image,
          hasInstructions: item.hasInstructions,
        };
        acc[group].children.push(item);

        return acc;
      }, {})
    );
  }

  groupFeatured(data) {
    return Object.values(
      data.reduce((acc, item) => {
        if (!item.hasInstructions) return acc;

        const group = this.featuredTitle;
        acc[group] ??= {
          group,
          children: [],
          first: item.first,
          image: item.image,
          hasInstructions: item.hasInstructions,
        };
        acc[group].children.push(item);

        return acc;
      }, {})
    );
  }

  render() {
    if (
      !this.groupedData ||
      !Array.isArray(this.groupedData) ||
      this.groupedData.length === 0
    ) {
      return;
    }

    const className = `card-container ${
      this.background === "#ffffff" ? "white" : ""
    }`;

    const tabsHTML = `
      <div class="custom-container">

        <div class="letter-list ${className}">
          ${this.groupedData
            .map(
              (group, index) => `
                <a data-tab-index="${index}" data-handle="${
                group.group
              }" aria-selected="${index === 0}" class="tab letters ${
                index === 0 ? "active featured" : ""
              }">
                    ${group.group}
                  </a>
              `
            )
            .join("")}
        </div>

        <div class="selected-brand">
          <h3 class="heading h4">Select a Brand</h3>
        </div>
        
        <div class="brand-container ${className}">
          <div class="clear-brand">
            <button class="button">Clear</button>
          </div>
          <div class="brands">
            ${this.groupedData
              .map(
                (group, index) => `
                      ${group.children
                        .map(
                          (brand, brandIndex) => `
                            <div data-tab-index="${index}" data-brand-index="${brandIndex}" class="brand-item">
                              <a href="#" data-handle="${
                                brand.handle
                              }" class="brand-link">
                                <div class="item" style="${
                                  brand.image
                                    ? ""
                                    : "justify-content:center;text-align:center;"
                                }">
                                  ${
                                    brand.image
                                      ? `<img src="${brand.image}">`
                                      : `${brand.name}`
                                  }
                                </div>
                              </a>
                            </div>
                          `
                        )
                        .join("")}
                  
                `
              )
              .join("")}
          </div>
        </div>

       <div class="content ${className}">
          ${this.groupedData
            .map(
              (group, index) => `
                <div data-tab-index="${index}" class="group">
                  
                </div>
              `
            )
            .join("")}
        </div>

      </div>
    `;

    const container = document.createElement("div");
    container.innerHTML = tabsHTML;
    this.shadowRoot.appendChild(container.firstElementChild);

    this.shadowRoot.querySelectorAll(".tab").forEach((tab) => {
      tab.addEventListener("click", this.handleTabClick.bind(this));
    });

    this.shadowRoot.querySelectorAll(".brand-item a").forEach((brandLink) => {
      brandLink.addEventListener("click", this.handleBrandClick);
    });

    this.shadowRoot.querySelector(".clear-brand button").addEventListener("click", () => {
     
      this.shadowRoot.querySelectorAll(".brand-item a").forEach((item) => {
        item.parentElement.classList.remove("hidden");
      }); 
    });
  }

  handleBrandClick = async (event) => {
    event.preventDefault();
    const handle = event.currentTarget.dataset.handle;
    const tabIndex = event.currentTarget.parentElement.dataset.tabIndex;
    const brandIndex = event.currentTarget.parentElement.dataset.brandIndex;

    this.shadowRoot.querySelector(".selected-brand").innerHTML = `
      <h3 class="heading h4">Selected: ${handle}</h3>
    `;

    this.shadowRoot.querySelectorAll(".brand-item a").forEach((item) => {
      if (item.dataset.handle !== handle) {
        item.parentElement.classList.add("hidden");
      }
    });

    const query = `
      query {
          collectionByHandle(handle: "${handle}") {
            products(first: 250) {
              edges {
                node {
                  title
                  images(first: 1) {
                    edges {
                      node {
                        url
                      }
                    }
                  }
                }
              }
            }
          }
      }
    `;

    // const data = await fetchData(query);
    // console.log(data);

    // this.shadowRoot
    //   .querySelectorAll(
    //     `.brand-group[data-tab-index="${tabIndex}"] .brand-item`
    //   )
    //   .forEach((item) => item.classList.remove("active"));

    // event.currentTarget.parentElement.classList.add("active");

    // if (!handle || tabIndex === null) return;

    const container = this.shadowRoot.querySelector(
      `.group[data-tab-index="${tabIndex}"]`
    );
    if (!container) return;

    container.innerHTML = "";

    const url = `${window.Shopify.routes.root}collections/${handle}?view=data`;
    const results = await this.fetchShopifyTemplate(url);
    container.innerHTML = results?.innerHTML || "";
  };

  handleTabClick = async (event) => {
    const index = event.currentTarget.dataset.tabIndex;

    this.shadowRoot.querySelectorAll(".tab").forEach((tab) => {
      tab.setAttribute("aria-selected", tab.dataset.tabIndex === index);
      tab.classList.toggle("active", tab.dataset.tabIndex === index);
    });

    this.shadowRoot.querySelectorAll(".brand-item").forEach((item) => {
      item.classList.toggle("hidden", item.dataset.tabIndex !== index);
    });

    this.shadowRoot.querySelectorAll(".group").forEach((group) => {
      group.classList.toggle("hidden", group.dataset.tabIndex !== index);
    });

    // this.shadowRoot.querySelectorAll(".brand-group").forEach((group) => {
    //   group.classList.toggle("hidden", group.dataset.tabIndex !== index);
    // });
  };
}

customElements.define("instructions-database", InstructionsDatabase);
